import { Grid, Paper, Tab, Tabs, Typography } from "@mui/material";
import * as dates from "common/dates";
import Notes from "components/common/Notes";
import PeerReviewNote from "components/common/PeerReviewNotes";
import Visible from "components/common/Visible";
import PriorYearNotes from "components/summary/notes/PriorYearNotes";
import * as labels from "config.labels";
import { COVERAGE_NOTES } from "flags";
import { useMemo, useState } from "react";
import { connect } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as submissionsService from "services/submissionsService";
import { updateClaimsNote } from "store/actions/input/claims/claimsActions";
import { updateExposureNote } from "store/actions/input/exposure/exposureActions";
import {
  updateNotes as updateProgramNotes,
  updateRiskSummary,
} from "store/actions/input/program/programActions";
import { updateScenariosNote } from "store/actions/input/scenarios/scenariosActions";
import { updatePeersNote } from "store/actions/peers/peersActions";
import {
  updateFrequencyPricingNote,
  updateLayerPricingNote,
} from "store/actions/pricing/pricingActions";
import { updateTowerPricingNote } from "store/actions/pricing/tower/towerPricingActions";
import * as summaryActions from "store/actions/summary/summaryActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as notesStore from "store/notes";
import { getInsured } from "store/selectors/input/program/programSelectors";
import * as notesSelectors from "store/selectors/notes/notesSelectors";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import { getPeerReviewer } from "store/selectors/review/reviewSelectors";
import * as staticDataSelectors from "store/selectors/temp/staticData/staticDataSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";
import * as utils from "utils";

const EditableNote = ({ title, value, context, update }) => (
  <Notes
    title={title}
    value={value}
    update={(text) => update(context, text)}
    rows={10}
    lg_width={6}
    md_width={12}
    growVertically
  />
);

const ReadOnlyNote = ({ title, value, hideIfEmpty }) => {
  if (hideIfEmpty && (!value || value.trim() === "")) {
    return <></>;
  }

  return (
    <Notes
      title={title}
      value={value}
      placeholder={""}
      disabled={true}
      rows={10}
      lg_width={6}
      md_width={12}
      growVertically
    />
  );
};

const SummaryNotes = (props) => {
  const [selectedTab, setSelectedTab] = useState(null);

  const showAllNotes = staticDataService.useFeatureFlag(
    "summary/notes/showAllNotes",
    { default: false }
  );

  const {
    data: renewalData,
  } = submissionsService.useListRenewalSubmissionsQuery(
    { ids: props.submissionId ? [props.submissionId] : [] },
    { skip: !props.submissionId }
  );

  const yearTabs = useMemo(() => {
    if (!renewalData?.submissions) return [];
    return Object.values(renewalData.submissions)
      .map((sub) => ({
        submissionId: sub.submissionId,
        year: dates.strDateToYearMonthDay(sub.effectiveDate ?? sub.inception)
          ?.year,
        isCurrent: sub.submissionId === props.submissionId,
      }))
      .filter((t) => t.year != null)
      .sort((a, b) => b.year - a.year);
  }, [renewalData, props.submissionId]);

  const showYearTabs = yearTabs.length > 1;
  const activeTabId = selectedTab ?? props.submissionId;
  const isCurrentSubmission =
    !showYearTabs || activeTabId === props.submissionId;
  const selectedPriorSubmissionId = isCurrentSubmission ? null : activeTabId;
  const { data: generalConfiguration } = staticDataService.useConfigQuery(
    "general_configuration"
  );

  const excludedNoteKeys =
    generalConfiguration?.summaryNotes?.excludedNoteKeys ?? [];

  const isExcluded = (key) =>
    Array.isArray(excludedNoteKeys) && excludedNoteKeys.includes(key);

  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const notesConfig = notesSelectors.translateNotesConfig(
    props.notesConfig,
    translateLabel
  );

  const editableNotes = notesConfig.filter(
    (note) =>
      note.section === "Summary" &&
      !["considerationsFromLastRenewal", "peerReview"].includes(note.id) &&
      !isExcluded(note.id)
  );

  const additionalNotes = notesConfig.filter(
    (note) =>
      note.section === "Additional Notes" &&
      !isExcluded(note.id) &&
      (showAllNotes || (props.notes?.[note.id] || "").trim() !== "")
  );

  const updateNoteByKey = (key, value) => {
    switch (key) {
      case "riskSummary":
        props.updateRiskSummary(value);
        break;
      case "program":
        props.updateProgramNotes(value);
        break;
      case "claims":
        props.updateClaimsNote(value);
        break;
      case "exposure":
        props.updateExposureNote(value);
        break;
      case "scenarios":
        props.updateScenariosNote(value);
        break;
      case "lossFrequency":
        props.updateFrequencyPricingNote(value);
        break;
      case "layerPricing":
        props.updateLayerPricingNote(value);
        break;
      case "towerPricing":
        props.updateTowerPricingNote(value);
        break;
      case "comparison":
        props.updatePeersNote(value);
        break;
      case "riskConsiderations":
      case "termsAndConditions":
      case "capacityUtilization":
      case "pricingComments":
      case "considerationsForNextRenewal":
        props.updateSummaryNote(key, value);
        break;
      case "coverageNotes":
      case "lossDevelopment":
        props.updateKeyedNote(key, value);
        break;
      default:
        if (
          typeof key === "string" &&
          key.startsWith("pricing.layerPricing.") &&
          key.endsWith(".explanation")
        ) {
          props.updateKeyedNote(key, value);
        }
        break;
    }
  };

  const uwJustificationNotes = isExcluded("uwSelectedJustification")
    ? []
    : (props.layers ?? [])
        .map((layer) => {
          const noteKey = `pricing.layerPricing.${layer.id}.explanation`;
          return {
            noteKey,
            title: `${translateLabel(labels.LAYER_PRICING_JUSTIFICATION_TITLE, {
              default: "UW Selected Justification",
            })} (${utils.shortLayerName(layer)})`,
            value: props.notesTexts?.[noteKey] ?? "",
          };
        })
        .filter((note) => showAllNotes || note.value.trim() !== "");

  return (
    <>
      {showYearTabs && (
        <Paper
          variant={"outlined"}
          sx={{
            width: "100%",
            marginTop: "1rem",
            marginLeft: "1.5rem",
            border: "none",
          }}
        >
          <Tabs
            value={activeTabId ?? false}
            onChange={(_, value) => setSelectedTab(value)}
            variant={"scrollable"}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            {yearTabs.map((tab) => (
              <Tab
                key={tab.submissionId}
                label={tab.isCurrent ? `${tab.year} (Current)` : `${tab.year}`}
                value={tab.submissionId}
              />
            ))}
          </Tabs>
        </Paper>
      )}

      {isCurrentSubmission ? (
        <>
          {editableNotes.map((note) => (
            <EditableNote
              key={note.id}
              title={note.label}
              value={props.notes?.[note.id] ?? ""}
              context={note.id}
              update={updateNoteByKey}
            />
          ))}

          <Visible byTag={COVERAGE_NOTES} defaultVisible={false}>
            <EditableNote
              title={"Coverage Notes"}
              value={props.coverageNotes ?? ""}
              context={"coverageNotes"}
              update={updateNoteByKey}
            />
          </Visible>
          {!isExcluded("considerationsFromLastRenewal") && (
            <ReadOnlyNote
              title={translateLabel(
                labels.SUMMARY_NOTES_CONSIDERATIONS_FROM_LAST_RENEWAL,
                { default: "Considerations from Last Renewal" }
              )}
              value={props.notes.considerationsFromLastRenewal}
            />
          )}
          {!isExcluded("peerReview") && (
            <Visible byTag={"summary.notes.peerReview"} defaultVisible={true}>
              <PeerReviewNote title={"Peer Reviewer Commentary"} {...props} />
            </Visible>
          )}
          {(additionalNotes.length > 0 || uwJustificationNotes.length > 0) && (
            <>
              <Grid item lg={12}>
                <Typography variant={"h5"}>{"Additional Notes"}</Typography>
              </Grid>

              {additionalNotes.map((note) => (
                <EditableNote
                  key={note.id}
                  title={note.label}
                  value={props.notes?.[note.id] ?? ""}
                  context={note.id}
                  update={updateNoteByKey}
                />
              ))}
              {uwJustificationNotes.map((note) => (
                <EditableNote
                  key={note.noteKey}
                  title={note.title}
                  value={note.value}
                  context={note.noteKey}
                  update={updateNoteByKey}
                />
              ))}
            </>
          )}
        </>
      ) : (
        <PriorYearNotes
          submissionId={selectedPriorSubmissionId}
          notesConfig={notesConfig}
        />
      )}
    </>
  );
};

const mapDispatchToProps = {
  updateRiskSummary,
  updateProgramNotes,
  updateClaimsNote,
  updateExposureNote,
  updateScenariosNote,
  updateFrequencyPricingNote,
  updateLayerPricingNote,
  updateTowerPricingNote,
  updatePeersNote,
  updateSummaryNote: summaryActions.updateSummaryNote,
  updateSummaryPeerReviewComment: summaryActions.updateSummaryPeerReviewComment,
  updateSummaryPeerReviewer: summaryActions.updateSummaryPeerReviewer,
  updateSummaryPeerReviewerFromName:
    summaryActions.updateSummaryPeerReviewerFromName,
  updateSummaryPeerReviewDate: summaryActions.updateSummaryPeerReviewDate,
  updateKeyedNote: (key, text) => notesStore.updateNoteText({ key, text }),
};

const mapStateToProps = (state) => {
  const peerReview = state?.summary?.peerReview ?? {};
  return {
    submissionId: persistenceSelectors.selectSubmissionId(state),
    notesConfig: notesSelectors.selectNotesConfig(state),
    notes: notesSelectors.selectNotes(state),
    coverageNotes: notesStore.selectNoteText("coverageNotes")(state),
    layers: pricingSelectors.selectLayers(state),
    notesTexts: state?.notes?.texts ?? {},
    peerReview,
    userEmail: userSelectors.selectUser(state).attributes?.email ?? "",
    underwriterList: staticDataSelectors.selectUnderwriterList(state),
    userIsUnderwriter:
      staticDataSelectors.selectUnderwriterFromUser(state) != null,
    underwriterFromUser: staticDataSelectors.selectUnderwriterFromUser(state),
    isUserReadOnly: userSelectors.isUserReadOnly(state),
    peerReviewer: getPeerReviewer(state),
    insured: getInsured(state),
    user: userSelectors.selectUser(state).attributes,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SummaryNotes);
